import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import { ArrowLeftIcon } from '@material-ui/pickers/_shared/icons/ArrowLeftIcon';
import { MDSTypography, MDSTheme } from '@marqvision/mds/core';
import { MenuIcon } from '@marqvision/mds/icons';
import { useAuth } from '~/apis/auth';
import { latestScrollY } from '~/atoms/myApp';
import useBreakpoint from '../../hooks/useBreakpoint';
import useGlobalAction from '../../hooks/useGlobalAction';
import useLanguage from '../../hooks/useLanguage';
import useRouteNames from '../../hooks/useRouteNames';
import CTAButton from '../home/CTAButton';
import { Logger } from '../../utils';
import FolioLogo from '../FolioLogo';
import Control from './Control';
import HeaderLink from './HeaderLink';
import Sidemenu from './Sidemenu';

export const HeaderHeight = '56px';

const useStyles = makeStyles(() => ({
  wrap: {
    flex: `0 0 ${HeaderHeight}`,
    position: 'sticky',
    top: 0,
    display: 'flex',
    width: '100%',
    height: HeaderHeight,
    padding: 0,
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    transition:
      'color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, fill 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    zIndex: 5,
  },
  link: {
    textDecoration: 'none',
  },
  backButton: {
    padding: 0,
    marginRight: '16px',
    color: 'white',
    backgroundColor: `rgba(255, 255, 255, 0.1)`,
    width: '26px',
    height: '26px',
  },
}));

interface IProps {
  variant?: 'normal' | 'transparent' | 'dark';
  paddingHorizontal?: string;
  menus?: boolean;
  ctaButton?: boolean;
  notification?: boolean;
  shadow?: boolean;
  hideLoginOrProfile?: boolean;
  hideChangeLanguage?: boolean;
  hideCurrency?: boolean;
  title?: string;
  color?: 'dark';
}

export const HeaderContext = React.createContext<IProps>({});

const Header = (props: IProps): JSX.Element => {
  const {
    variant = 'normal',
    paddingHorizontal = '16px',
    ctaButton = false,
    menus = false,
    shadow = false,
    title,
  } = props;

  const classes = useStyles();

  const RouteNames = useRouteNames();
  const { tablet, desktop } = useBreakpoint();

  const { pathname } = useLocation();
  const { login } = useGlobalAction();
  const { Lang } = useLanguage();
  const navigate = useNavigate();
  const { data: authData } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const setScrollY = useSetAtom(latestScrollY);
  const location = useLocation();

  const hasBackButton = desktop && location.pathname.indexOf('/application/my/') >= 0;
  const isInHome = pathname === RouteNames.Home;

  const getButtonColor = (variant: 'normal' | 'transparent' | 'dark') => (variant === 'normal' ? 'bluegray' : 'white');

  const renderLogo = () => {
    const logo = <FolioLogo color={variant === 'normal' ? 'black' : 'white'} />;

    const render = isInHome
      ? (children: React.ReactNode) => (
          <a href={'#'} style={{ height: '16px' }}>
            {children}
          </a>
        )
      : (children: React.ReactNode) => (
          <Link to={RouteNames.Home} style={{ height: '16px' }}>
            {children}
          </Link>
        );

    return (
      <>
        {tablet && (
          <IconButton onClick={() => setIsMenuOpen((ps) => !ps)} style={{ margin: '0 12px' }}>
            <MenuIcon color={variant === 'normal' ? 'black' : 'white'} />
          </IconButton>
        )}
        {hasBackButton && (
          <IconButton className={classes.backButton} onClick={() => navigate(-1)}>
            <ArrowLeftIcon />
          </IconButton>
        )}
        {title ? (
          <MDSTypography
            weight="medium"
            variant="T20"
            color={variant === 'normal' ? MDSTheme.palette.bluegray[800] : 'white'}
          >
            {title}
          </MDSTypography>
        ) : (
          render(logo)
        )}
      </>
    );
  };

  return (
    <div
      className={classes.wrap}
      style={{
        boxShadow:
          variant !== 'dark' && shadow ? '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 8px rgba(0, 0, 0, 0.12)' : 'none',
        backgroundColor:
          variant === 'normal'
            ? MDSTheme.palette.white
            : variant === 'dark'
            ? MDSTheme.palette.bluegray[850]
            : 'transparent',
      }}
    >
      {!desktop && (
        <Sidemenu
          variant={'light'}
          DrawerProps={{ variant: 'temporary', open: isMenuOpen, onClose: () => setIsMenuOpen(false) }}
          onClose={() => setIsMenuOpen(false)}
        />
      )}
      <div
        className={classes.link}
        style={{
          position: 'absolute',
          left: desktop ? paddingHorizontal : 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {renderLogo()}
        {ctaButton && <CTAButton size="medium" style={{ marginLeft: '24px' }} />}
      </div>

      {/* Menus */}
      {desktop && menus && (
        <Box display="flex" justifyContent="center" alignItems="center" gridGap="36px" flex="1">
          <HeaderLink
            to={RouteNames.FileAnApplication}
            color={getButtonColor(variant)}
            onClick={() =>
              Logger.track('clicked file an application', {
                Through: 'LNB-File an application',
              })
            }
          >
            {Lang.FileAnApplication}
          </HeaderLink>
          <HeaderLink
            to={RouteNames.MyApplication}
            color={getButtonColor(variant)}
            login={login}
            onClick={() => {
              setScrollY(0);
              Logger.track('clicked my application');
            }}
          >
            {Lang.MyApplication}
          </HeaderLink>
          <HeaderLink
            to={RouteNames.GlobalStatus}
            color={getButtonColor(variant)}
            onClick={(e) => {
              Logger.track('clicked global ip status');
              if (!authData?.accessToken) {
                e.preventDefault();
                login(() => navigate(RouteNames.GlobalStatus));
              }
            }}
          >
            {Lang.IpManagement}
          </HeaderLink>
        </Box>
      )}

      <HeaderContext.Provider value={props}>
        <Control />
      </HeaderContext.Provider>
    </div>
  );
};

export default Header;
